import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Card } from '../../elements/ui/card';
import { Button } from '../../elements/ui/button';
import { Skeleton } from '../../elements/ui/skeleton';
import { useDeviceSyncStatus } from '../../hooks/useDeviceSyncStatus';
import { DeviceSyncStatusItem, SYNC_STUCK_AFTER_HOURS, getSyncSeverity } from '../../models/device-sync-status';
import { deviceSyncColors } from './colors';
import DeviceSyncTable from './DeviceSyncTable';

/**
 * One headline number, in the shape the piglet stat cards established: grey label, ink value, and a caption
 * whose colour dot carries the judgement.
 *
 * The dot is the whole point. Everywhere else on this dashboard the number stays `text-gray-900` and a 1.5px
 * dot says whether it is good news - so a coloured figure here would be the only one on the page, and would
 * read as a different kind of thing rather than as a worse one.
 */
const StatTile: React.FC<{
  label: string;
  value: number;
  caption: string;
  dotColor: string;
}> = ({ label, value, caption, dotColor }) => (
  <div className="px-5 py-5">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">{value}</p>
    <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
      <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
      {caption}
    </p>
  </div>
);

/** Mirrors the tiles line for line - same paddings, same line heights - so nothing shifts when data lands. */
const WidgetSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 border-t border-gray-200 sm:grid-cols-3 sm:divide-x sm:divide-gray-200">
    {[0, 1, 2].map((index) => (
      <div key={index} className="px-5 py-5">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-2 h-9 w-16" />
        <Skeleton className="mt-2 h-4 w-32" />
      </div>
    ))}
  </div>
);

/**
 * The farm's unsent mobile backlog: animals a phone registered in a barn and has not managed to hand to the
 * backend yet.
 *
 * Last on the dashboard and quiet by default. It is a caveat on the figures above rather than another set of
 * them, so it takes the card-level type scale (`text-sm` title, like the animal cards) rather than the
 * section scale the chart card uses - and it removes itself entirely on a farm whose phones have never
 * reported, so it costs nothing on a farm that does not use the offline queue.
 *
 * Severity is read off how long a queue has been standing, not how big it is: a phone that just registered
 * 200 animals out of signal is normal, the same 200 still there tomorrow is not (see `getSyncSeverity`).
 * Devices that have caught up fold away behind a toggle - the list exists to be acted on.
 */
const DeviceSyncStatus: React.FC<{ farmId?: string }> = ({ farmId }) => {
  const { t } = useTranslation();
  const { overview, isBusy, loaded, reload } = useDeviceSyncStatus(farmId);
  const [showDrained, setShowDrained] = React.useState(false);

  const pending = React.useMemo(
    () => (overview?.devices ?? []).filter((device) => device.pendingCount > 0),
    [overview]
  );

  // A farm whose phones have never reported has nothing to show and never will until one does; the card
  // would just be an empty panel at the foot of the dashboard forever.
  if (loaded && (!overview || overview.deviceCount === 0)) {
    return null;
  }

  const stuckCount = pending.filter((device) => getSyncSeverity(device) === 'stuck').length;

  const rows: DeviceSyncStatusItem[] = showDrained ? (overview?.devices ?? []) : pending;

  /** Green when there is nothing to do, red once something has been standing past the stuck threshold. */
  const dotFor = (count: number, escalated: boolean) => {
    if (count === 0) {
      return deviceSyncColors.draining;
    }

    return escalated ? deviceSyncColors.stuck : deviceSyncColors.delayed;
  };

  const renderBody = () => {
    if (!loaded || !overview) {
      return <WidgetSkeleton />;
    }

    return (
      <>
        <div className="grid grid-cols-1 border-t border-gray-200 sm:grid-cols-3 sm:divide-x sm:divide-gray-200">
          <StatTile
            label={t('f-device-sync-stat-pending')}
            value={overview.totalPendingCount}
            caption={t('f-device-sync-stat-pending-hint')}
            dotColor={dotFor(overview.totalPendingCount, stuckCount > 0)}
          />
          <StatTile
            label={t('f-device-sync-stat-devices')}
            value={overview.pendingDeviceCount}
            caption={t('f-device-sync-stat-devices-hint', { count: overview.deviceCount })}
            dotColor={dotFor(overview.pendingDeviceCount, stuckCount > 0)}
          />
          <StatTile
            label={t('f-device-sync-stat-errors')}
            value={overview.failingDeviceCount}
            caption={t('f-device-sync-stat-errors-hint')}
            dotColor={dotFor(overview.failingDeviceCount, overview.failingDeviceCount > 0)}
          />
        </div>

        {overview.pendingDeviceCount === 0 ? (
          // No icon here. The three green dots above have already said it; a fourth marker would be the
          // loudest thing on the quietest card.
          <p className="border-t border-gray-200 px-5 py-4 text-xs text-gray-500">
            {t('f-device-sync-all-clear', { count: overview.deviceCount })}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-5 py-3">
              <p className="text-xs text-gray-500">
                {t('f-device-sync-behind-summary', { count: overview.pendingDeviceCount })}
                {stuckCount > 0 && (
                  <>
                    {' · '}
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        aria-hidden
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: deviceSyncColors.stuck }}
                      />
                      {t('f-device-sync-stuck-summary', { count: stuckCount, hours: SYNC_STUCK_AFTER_HOURS })}
                    </span>
                  </>
                )}
              </p>

              {/* Only offered when there is something to unfold - on a fleet where every device is behind,
                  the toggle would change nothing. */}
              {overview.deviceCount > overview.pendingDeviceCount && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDrained((open) => !open)}
                  aria-expanded={showDrained}
                >
                  {t(showDrained ? 'f-device-sync-hide-synced' : 'f-device-sync-show-synced')}
                </Button>
              )}
            </div>

            <DeviceSyncTable devices={rows} />
          </>
        )}
      </>
    );
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
        <h3 className="min-w-0 text-sm font-semibold text-gray-900">{t('f-device-sync-title')}</h3>

        {/* Every native button style is reset explicitly - this project loads Tailwind's theme and utilities
            without preflight, so a bare <button> keeps the browser's grey face and border. */}
        <button
          type="button"
          onClick={reload}
          disabled={isBusy}
          title={t('f-reload')}
          aria-label={t('f-reload')}
          className="flex h-8 w-8 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-md border border-gray-200 bg-white p-0 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-default disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isBusy ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {renderBody()}
    </Card>
  );
};

export default DeviceSyncStatus;
